// Settings.tsx - Complete file with CVU state management
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Form, Button, Row, Col, Menu, message, Typography, Divider, Tooltip } from 'antd';
import {
  UserOutlined,
  MessageOutlined,
  LockOutlined,
  InboxOutlined,
  BankOutlined,
  SaveOutlined,
  DollarOutlined,
  ReloadOutlined,
  SettingOutlined,
  LeftOutlined,
  AppstoreOutlined,
  WalletOutlined,
  ApiOutlined
} from '@ant-design/icons';
import { motion } from 'framer-motion';
import UserSettings from './UserSettings';
import MessageSettings from './MessageSettings';
import CredentialSettings from './CredentialSettings';
import RequestSettings from './RequestSettings';
import WithdrawSettings from './WithdrawSettings';
import CvuSettings from './CvuSettings';
import { AdminSettings } from './AdminSettings';
import { PanelSettings } from './PanelSettings';
import { WalletSettings } from './WalletSettings';
import { EndpointSettings } from './EndpointSettings';
import { getAndSanitize } from '../../shared/utils/sanitization';
import type { MenuProps } from 'antd';
import type { Platform } from './types';
import styled from 'styled-components';
import { useSettingsStore } from '../../shared/stores/useSettingsStore';
import { useTranslation } from 'react-i18next';
import { useEffectiveAdminId } from '../../contexts/AdminSelectorContext';
import { useOutletContext } from 'react-router-dom';

// Define context type from UserPanel
interface UserPanelOutletContext {
  activeSettingsTab?: string;
  onSettingsTabChange?: (tab: string) => void;
}

const { Title } = Typography;

// TypeScript interfaces
interface CvuConfig {
  name: string;
  number: string;
  holder: string;
  active: boolean;
  startTime: string | null;
  endTime: string | null;
}

interface SettingsFormData {
  minUsernameLength: number;
  maxUsernameLength: number;
  minPasswordLength: number;
  maxPasswordLength: number;
  useDefaultPassword: boolean;
  defaultPassword: string;
  textMessage: string;
  messageFlowId: string;
  useMessageFlow: boolean;
  testUsername: string;
  credentialFormat: string;
  autoProcessReceipts: boolean;
  autoProcessBonus: number;
  autoProcessBonusType: 'fixed' | 'percentage';
  autoProcessBonusLoads: number;
  minValidationScore: number;
  loadRejectedMessage: string;
  loadRejectedFlowId: string;
  useLoadRejectedFlow: boolean;
  duplicateReceiptMessage: string;
  platformConfigs: Record<string, any>;
  cvuConfigs: Record<string, CvuConfig>;
  cvuSystemEnabled: boolean;
  withdrawMessage: string;
  withdrawFlowId: string;
  useWithdrawFlow: boolean;
  withdrawApprovedMessage: string;
  withdrawConfirmationMessage: string;
  withdrawRejectedMessage: string;
}

// Componentes estilizados para mejorar la UI
const MenuContainer = styled.div`
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.07);
  background: var(--color-container-dark);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  }

  [data-theme="light"] & {
    background: var(--color-container-light);
    
    &:hover {
      .ant-typography, h4, p, span, .ant-menu-item:not(.ant-menu-item-selected) {
        color: var(--color-text-primary-light, #000000df) !important;
      }
    }
  }
`;

const ContentPanel = styled(motion.div)`
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.07);
  background: var(--color-container-dark);
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  }

  [data-theme="light"] & {
    background: var(--color-container-light);
    
    &:hover {
      .ant-typography, h4, p, span, .ant-menu-item:not(.ant-menu-item-selected) {
        color: var(--color-text-primary-light, #000000df) !important;
      }
    }
  }
`;

const SettingsContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding-bottom: 80px;
`;

const SectionHeader = styled.div`
  padding: 18px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: linear-gradient(to right, rgba(24, 144, 255, 0.08), rgba(24, 144, 255, 0.02));

  [data-theme="light"] & {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    background: linear-gradient(to right, rgba(24, 144, 255, 0.08), rgba(24, 144, 255, 0.02));
  }
`;

const SectionContent = styled(motion.div)`
  padding: 24px;
`;

const StyledMenu = styled(Menu)`
  background: transparent !important;
  padding: 16px 12px;

  .ant-menu-item {
    border-radius: 10px;
    margin: 6px 0 !important;
    height: 48px;
    line-height: 48px;
    transition: all 0.3s ease;
    font-weight: 500;
    color: var(--color-text-secondary);
  }

  .ant-menu-item-icon {
    font-size: 18px;
  }

  .ant-menu-item-active {
    background-color: rgba(24, 144, 255, 0.08) !important;
    color: #ac4949 !important;
  }

  .ant-menu-item-selected {
    background: linear-gradient(90deg, #ac4949, #8b3333) !important;
    color: white !important;
    font-weight: 500;
  }
`;

const ActionButton = styled(Button)`
  border-radius: 8px;
  height: 40px;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
  
  &.ant-btn-primary {
    background: linear-gradient(90deg, #ac4949, #8b3333);
    border: none;
    
    &:hover {
      background: linear-gradient(90deg, #8b3333, #ac4949);
    }
  }
`;

const SUPPORTED_PLATFORMS: Platform[] = [
  { id: 'macro', label: 'Macro' },
  { id: 'brubank', label: 'Brubank' },
  { id: 'belo', label: 'Belo' },
  { id: 'naranjax', label: 'Naranja X' },
  { id: 'mercadopagoAlt', label: 'Mercado Pago Alt' },
  { id: 'personalpayAlt', label: 'Personal Pay Alt' },
  { id: 'mercadopago', label: 'Mercado Pago' },
  { id: 'personalpay', label: 'Personal Pay' },
  { id: 'cuentadni', label: 'Cuenta DNI' },
  { id: 'uala', label: 'Uala' },
  { id: 'galicia', label: 'Galicia' },
  { id: 'supervielle', label: 'Supervielle' },
  { id: 'bna', label: 'BNA' },
  { id: 'astropay', label: 'AstroPay' },
  { id: 'ripio', label: 'Ripio' },
  { id: 'rioja', label: 'Rioja' },
  { id: 'credicoop', label: 'Credicoop' },
  { id: 'nbch', label: 'Banco del Chaco' },
  { id: 'bancosol', label: 'Banco del Sol' },
  { id: 'prex', label: 'Prex' },
] as const;

type MenuKey = 'user' | 'message' | 'credentials' | 'requests' | 'cvu' | 'withdraw' | 'admins' | 'panels' | 'wallets' | 'endpoints';

// Sections that require admin selection for SuperAdmin (defined outside component to avoid re-creation)
const ADMIN_REQUIRED_SECTIONS: MenuKey[] = ['user', 'message', 'credentials', 'requests', 'cvu', 'withdraw'];

// Definir las secciones de Settings con sus íconos y colores
export const settingsPages = [
  {
    key: "admins",
    label: "Admin Management",
    icon: <UserOutlined />,
    color: "#1890ff",
    description: "Manage administrator accounts and permissions",
    superAdminOnly: true,
  },
  {
    key: "user",
    label: "Users",
    icon: <UserOutlined />,
    color: "#52c41a",
    description: "User configuration settings",
  },
  {
    key: "message",
    label: "Messages",
    icon: <MessageOutlined />,
    color: "#fa8c16",
    description: "Message templates and flows",
  },
  {
    key: "credentials",
    label: "Credentials",
    icon: <LockOutlined />,
    color: "#722ed1",
    description: "Credential format settings",
  },
  {
    key: "requests",
    label: "Requests",
    icon: <InboxOutlined />,
    color: "#eb2f96",
    description: "Request processing settings",
  },
  {
    key: "cvu",
    label: "CVU",
    icon: <BankOutlined />,
    color: "#13c2c2",
    description: "CVU configuration",
  },
  {
    key: "withdraw",
    label: "Withdrawals",
    icon: <DollarOutlined />,
    color: "#faad14",
    description: "Withdrawal settings",
  },
  {
    key: "panels",
    label: "Panels",
    icon: <AppstoreOutlined />,
    color: "#1877f2",
    description: "Manage panels",
  },
  {
    key: "wallets",
    label: "Wallets",
    icon: <WalletOutlined />,
    color: "#f5222d",
    description: "Wallet management",
  },
  {
    key: "endpoints",
    label: "Endpoints",
    icon: <ApiOutlined />,
    color: "#531dab",
    description: "API endpoint configuration",
  },
];

export const Settings: React.FC = () => {
  // Get context from UserPanel
  const context = useOutletContext<UserPanelOutletContext>();

  const [form] = Form.useForm<SettingsFormData>();
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [originalValues, setOriginalValues] = useState<SettingsFormData | null>(null);
  const [currentSection, setCurrentSection] = useState<MenuKey>(
    (context?.activeSettingsTab as MenuKey) || 'user'
  );

  // Sync with parent component
  useEffect(() => {
    if (context?.activeSettingsTab) {
      setCurrentSection(context.activeSettingsTab as MenuKey);
    }
  }, [context?.activeSettingsTab]);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [showSectionContent, setShowSectionContent] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [autoSaving, setAutoSaving] = useState<boolean>(false);

  // CVU State Management
  const [cvuConfigs, setCvuConfigs] = useState<Record<string, CvuConfig>>({});

  const { fetchSettings, updateSettings } = useSettingsStore();
  const { t } = useTranslation();
  const effectiveAdminId = useEffectiveAdminId();

  // Check if user is SuperAdmin
  const role = getAndSanitize('role');
  const isSuperAdmin = role === 'SUPERADMIN';

  // Auto-save timeout ref
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check if mobile device
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Animation for section changes
  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        type: "spring", 
        stiffness: 300, 
        damping: 20 
      }
    },
    exit: { 
      opacity: 0, 
      y: -20,
      transition: { duration: 0.2 }
    }
  };

  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Check if current section requires admin selection for SuperAdmin
        const requiresAdminSelection = isSuperAdmin && ADMIN_REQUIRED_SECTIONS.includes(currentSection);

        // If SuperAdmin is in a section that requires admin selection but no admin is selected, don't load
        if (requiresAdminSelection && !effectiveAdminId) {
          setLoading(false);
          setOriginalValues(null);
          form.resetFields();
          return;
        }

        setLoading(true);
        const response = await fetchSettings(effectiveAdminId || undefined);

        if (response?.data) {
          const settingsData = response.data.data || response.data;


          const normalizedData: SettingsFormData = {
            ...settingsData,
            useDefaultPassword: !!settingsData.useDefaultPassword,
            credentialFormat: settingsData.credentialFormat || 'Usuario: {username}\\nClave: {password}',
            cvuConfigs: settingsData.cvuConfigs || {},
            cvuSystemEnabled: settingsData.cvuSystemEnabled ?? true
          };


          form.setFieldsValue(normalizedData);
          setOriginalValues(normalizedData);
          setCvuConfigs(normalizedData.cvuConfigs);
          setHasChanges(false);
        }
      } catch (error) {
        console.error('Failed to fetch settings:', error);
        message.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [fetchSettings, form, effectiveAdminId, isSuperAdmin, currentSection]);

  useEffect(() => {
    if (originalValues && !hasChanges) {
      form.setFieldsValue(originalValues);
    }
  }, [currentSection, form, originalValues, hasChanges]);

  const onFinish = async (values: SettingsFormData): Promise<void> => {
    try {
      setLoading(true);

      // NOTE: CVU configs are excluded here because they auto-save individually
      // Including them would cause normalizeCvuConfigs to overwrite names with keys

      const { cvuConfigs: _, ...valuesWithoutCvu } = values;

      const finalValues = {
        ...valuesWithoutCvu,
        adminId: originalValues ? (originalValues as any).adminId : undefined
      };

      await updateSettings({
        settingsData: finalValues,
      });

      message.success(t('settings.saveSuccess'));
      setHasChanges(false);
      // Keep cvuConfigs in originalValues for state tracking
      setOriginalValues({ ...finalValues, cvuConfigs } as SettingsFormData);
    } catch (error) {
      console.error('Error saving settings:', error);
      message.error(t('settings.saveError'));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = (): void => {
    if (originalValues) {
      form.setFieldsValue(originalValues);
      setCvuConfigs(originalValues.cvuConfigs);
      setHasChanges(false);
    } else {
      form.resetFields();
    }
    message.info(t('settings.resetButton'));
  };

  const handleBackToList = () => {
    setShowSectionContent(false);
  };

  // Handle CVU config changes
  // NOTE: CVU configs are saved directly via upsertCvuConfigStore in CvuSettings
  // They should NOT trigger the general auto-save to avoid normalization issues
  const handleCvuConfigsChange = (newConfigs: Record<string, CvuConfig>) => {
    setCvuConfigs(newConfigs);
    // DO NOT set hasChanges - CVUs auto-save individually
  };

  // Auto-save function with debounce
  const autoSave = useCallback(async () => {
    if (!hasChanges || !originalValues) return;

    try {
      setAutoSaving(true);
      const values = form.getFieldsValue();

      // NOTE: CVU configs are excluded here because they auto-save individually
      // Including them would cause normalizeCvuConfigs to overwrite names with keys

      const { cvuConfigs: _, ...valuesWithoutCvu } = values;

      const finalValues = {
        ...valuesWithoutCvu,
        adminId: (originalValues as any).adminId
      };

      await updateSettings({
        settingsData: finalValues,
      });

      setHasChanges(false);
      // Keep cvuConfigs in originalValues for state tracking
      setOriginalValues({ ...finalValues, cvuConfigs } as SettingsFormData);
      message.success(t('settings.autoSaveSuccess') || 'Settings saved automatically', 1);
    } catch (error) {
      console.error('Error auto-saving settings:', error);
      message.error(t('settings.autoSaveError') || 'Failed to auto-save settings');
    } finally {
      setAutoSaving(false);
    }
  }, [hasChanges, originalValues, form, cvuConfigs, updateSettings, t]);

  // Auto-save effect with debounce
  useEffect(() => {
    if (hasChanges && originalValues) {
      // Clear existing timeout
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }

      // Set new timeout for 2 seconds
      autoSaveTimeoutRef.current = setTimeout(() => {
        autoSave();
      }, 2000);
    }

    // Cleanup
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [hasChanges, autoSave, originalValues]);

  const items: MenuProps['items'] = [
    // SuperAdmin-only sections
    ...(isSuperAdmin ? [{
      key: 'admins',
      icon: <UserOutlined />,
      label: 'Admins',
    }] : []),
    // Regular config sections
    {
      key: 'user',
      icon: <UserOutlined />,
      label: t('settings.navBar.userSetings'),
    },
    {
      key: 'message',
      icon: <MessageOutlined />,
      label: t('settings.navBar.messageSettings'),
    },
    {
      key: 'credentials',
      icon: <LockOutlined />,
      label: t('settings.navBar.credentialSettings'),
    },
    {
      key: 'requests',
      icon: <InboxOutlined />,
      label: t('settings.navBar.requestSettings'),
    },
    {
      key: 'cvu',
      icon: <BankOutlined />,
      label: t('settings.navBar.cvuSettings'),
    },
    {
      key: 'withdraw',
      icon: <DollarOutlined />,
      label: t('settings.navBar.withdrawSettings'),
    },
    // Resource management sections (visible to Admin and SuperAdmin)
    {
      key: 'panels',
      icon: <AppstoreOutlined />,
      label: 'Panels',
    },
    {
      key: 'wallets',
      icon: <WalletOutlined />,
      label: 'Wallets',
    },
    {
      key: 'endpoints',
      icon: <ApiOutlined />,
      label: 'Endpoints',
    },
  ];

  // Render content based on current section
  const renderContent = () => {
    // Check if SuperAdmin needs to select an admin for this section
    const requiresAdminSelection = isSuperAdmin && ADMIN_REQUIRED_SECTIONS.includes(currentSection);

    if (requiresAdminSelection && !effectiveAdminId) {
      return (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '300px'
        }}>
          <UserOutlined style={{ fontSize: '48px', color: '#ac4949', marginBottom: '16px' }} />
          <Title level={4} style={{ color: '#ac4949' }}>
            Selecciona un Admin
          </Title>
          <Typography.Text type="secondary" style={{ maxWidth: '400px', marginTop: '8px' }}>
            Para modificar configuraciones de usuario, mensajes, credenciales, solicitudes, CVU o retiros,
            primero debes seleccionar un administrador del sidebar.
          </Typography.Text>
        </div>
      );
    }

    switch (currentSection) {
      case 'admins':
        return <AdminSettings />;
      case 'user':
        return <UserSettings form={form} />;
      case 'message':
        return <MessageSettings form={form} />;
      case 'credentials':
        return <CredentialSettings form={form} />;
      case 'requests':
        return <RequestSettings platforms={SUPPORTED_PLATFORMS} />;
      case 'cvu':
        return (
          <CvuSettings
            form={form}
            cvuConfigs={cvuConfigs}
            setCvuConfigs={handleCvuConfigsChange}
          />
        );
      case 'withdraw':
        return <WithdrawSettings form={form} />;
      case 'panels':
        return <PanelSettings />;
      case 'wallets':
        return <WalletSettings />;
      case 'endpoints':
        return <EndpointSettings />;
      default:
        return null;
    }
  };

  const getSectionTitle = () => {
    switch(currentSection) {
      case 'admins': return 'Admin Management';
      case 'user': return t('settings.userSettings.title');
      case 'message': return t('settings.messageSettings.title');
      case 'credentials': return t('settings.credentialFormat.title');
      case 'requests': return t('settings.requestSettings.title');
      case 'cvu': return t('settings.cvuSettings.title');
      case 'withdraw': return t('settings.withdrawSettings.title');
      case 'panels': return 'Panel Management';
      case 'wallets': return 'Wallet Management';
      case 'endpoints': return 'Endpoint Management';
      default: return t('settings.title');
    }
  };

  const getSectionSubtitle = () => {
    switch(currentSection) {
      case 'admins': return 'Manage administrator accounts';
      case 'user': return t('settings.userSettings.description');
      case 'message': return t('settings.messageSettings.description');
      case 'credentials': return t('settings.credentialFormat.description');
      case 'requests': return t('settings.requestSettings.description');
      case 'cvu': return t('settings.cvuSettings.description');
      case 'withdraw': return t('settings.withdrawSettings.description');
      case 'panels': return 'Manage casino panels';
      case 'wallets': return 'Manage wallets for transfers';
      case 'endpoints': return 'Manage panel endpoints and notifications';
      default: return t('settings.subtitle');
    }
  };

  const renderMobileMenu = () => {
    return (
      <MenuContainer>
        <SectionHeader>
          <Title level={4} style={{ margin: 0, display: 'flex', alignItems: 'center', fontWeight: 600 }}>
            <SettingOutlined style={{ marginRight: 12, fontSize: '20px', color: '#ac4949' }} /> 
            Configuration
          </Title>
        </SectionHeader>

        <StyledMenu
          mode="vertical"
          selectedKeys={[currentSection]}
          items={items}
          onClick={({ key }) => {
            const newSection = key as MenuKey;
            setCurrentSection(newSection);
            setShowSectionContent(true);
            if (context?.onSettingsTabChange) {
              context.onSettingsTabChange(newSection);
            }
          }}
        />
      </MenuContainer>
    );
  };

  const renderMobileSectionContent = () => {
    return (
      <ContentPanel>
        <SectionHeader>
          <Row justify="space-between" align="middle">
            <Col>
              <Button 
                type="text" 
                icon={<LeftOutlined />} 
                onClick={handleBackToList}
                style={{ marginLeft: -12, marginRight: 8 }}
              >
                Volver
              </Button>
              <Title level={4} style={{ margin: 0, fontWeight: 600, display: 'inline-flex', alignItems: 'center' }}>
                {getSectionTitle()}
              </Title>
            </Col>
            <Col>
              {loading &&
                <Typography.Text type="secondary">
                  <ReloadOutlined spin /> Loading...
                </Typography.Text>
              }
              {autoSaving &&
                <Typography.Text type="secondary" style={{ color: '#52c41a' }}>
                  <ReloadOutlined spin /> Auto-saving...
                </Typography.Text>
              }
            </Col>
          </Row>
          <Typography.Text type="secondary" style={{ marginTop: 8, display: 'block' }}>
            {getSectionSubtitle()}
          </Typography.Text>
        </SectionHeader>

        <Form
            form={form}
            onFinish={onFinish}
            layout="vertical"
            onValuesChange={() => {
              if (originalValues) {
                setHasChanges(true);
              }
            }}
          >
            <SectionContent
              variants={contentVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {renderContent()}

              <Divider style={{ margin: '32px 0 24px' }} />

              <Row justify="end" align="middle" gutter={16}>
                <Col>
                  <Tooltip title="Reset to last saved settings">
                    <ActionButton
                      onClick={handleReset}
                      icon={<ReloadOutlined />}
                      disabled={loading || !hasChanges}
                    >
                      Reset
                    </ActionButton>
                  </Tooltip>
                </Col>
                <Col>
                  <Tooltip title={hasChanges ? "Changes will auto-save in 2 seconds" : "No changes to save"}>
                    <ActionButton
                      type="primary"
                      icon={<SaveOutlined />}
                      onClick={() => form.submit()}
                      loading={loading || autoSaving}
                      disabled={!hasChanges}
                    >
                      {autoSaving ? 'Saving...' : 'Save Now'}
                    </ActionButton>
                  </Tooltip>
                </Col>
              </Row>
            </SectionContent>
          </Form>
      </ContentPanel>
    );
  };

  return (
    <SettingsContainer>
      {isMobile ? (
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
            >
            </motion.div>
            
            {!showSectionContent ? (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                {renderMobileMenu()}
              </motion.div>
            ) : (
              <motion.div 
                key={currentSection}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                {renderMobileSectionContent()}
              </motion.div>
            )}
          </Col>
        </Row>
      ) : (
        <motion.div
          key={currentSection}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{ padding: '24px' }}
        >
          <Form
              form={form}
              onFinish={onFinish}
              layout="vertical"
              onValuesChange={() => {
                if (originalValues) {
                  setHasChanges(true);
                }
              }}
            >
              {renderContent()}

              <Divider style={{ margin: '32px 0 24px' }} />

              <Row justify="end" align="middle" gutter={16}>
                <Col>
                  <Tooltip title="Reset to last saved settings">
                    <ActionButton
                      onClick={handleReset}
                      icon={<ReloadOutlined />}
                      disabled={loading || !hasChanges}
                    >
                      Reset
                    </ActionButton>
                  </Tooltip>
                </Col>
                <Col>
                  <Tooltip title={hasChanges ? "Changes will auto-save in 2 seconds" : "No changes to save"}>
                    <ActionButton
                      type="primary"
                      icon={<SaveOutlined />}
                      onClick={() => form.submit()}
                      loading={loading || autoSaving}
                      disabled={!hasChanges}
                    >
                      {autoSaving ? 'Saving...' : 'Save Now'}
                    </ActionButton>
                  </Tooltip>
                </Col>
              </Row>
            </Form>
        </motion.div>
      )}
    </SettingsContainer>
  );
};